import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { usePrintWindow } from '@/hooks/usePrintWindow'
import { useRouteOrganizationId } from '@/hooks/useRouteOrganizationId'
import { BetaFeatureKey, hasBetaFeature } from '@/lib/beta-features'
import { organizationQueries } from '@/lib/services/organizations/organizations.query'
import { salesOrderQueries } from '@/lib/services/sales-orders/sales-orders.query'
import {
  hasConfiguredLocalPrint,
  localPrintNeedsPrinterCheck,
  printLocalReceipt,
  printLocalSalesOrder,
  subscribeToLocalPrintProfileUpdates,
} from '@/lib/local-print'
import type { LocalPrintDocumentKind } from '@/lib/local-print'
import { localPrintToastOptions } from '@/lib/local-print-ui'
import { salesOrderPrintTemplates } from '@/lib/services/sales-orders/sales-order-print-templates'
import type { SalesOrderPrintTemplate as SalesOrderPrintTemplateValue } from '@/lib/services/snapdragon/snapdragon.types'
import type { SuperTicketPreview } from '@/lib/services/super-ticket-settings/super-ticket-settings.types'
import { cn, getErrorMessage, readProperty } from '@/lib/utils'
import { useQueryClient } from '@tanstack/react-query'
import { ChevronDown, Printer, ReceiptText } from 'lucide-react'
import { type MouseEvent, useEffect, useState } from 'react'
import { toast } from 'sonner'
import LoadingButton, { type LoadingButtonProps } from '@/components/ui/loading-button'
import { SuperTicketPrintDialog, type SuperTicketPrintRequest } from './SuperTicketPrintDialog'

export { getSuperTicketOverflow, hasSuperTicketOverflow } from '@/lib/super-ticket-overflow'
export const SalesOrderPrintTemplate = salesOrderPrintTemplates
type PrintOption = {
  label: string
  template: SalesOrderPrintTemplateValue
  requiresConfiguration?: boolean
}

const printOptions: PrintOption[] = [
  {
    label: 'Super Ticket',
    template: SalesOrderPrintTemplate.JhcFb7373SuperTicket,
    requiresConfiguration: true,
  },
  { label: 'Order Summary', template: SalesOrderPrintTemplate.OrderSummary },
]

type LocalPrintOptions = {
  template?: SalesOrderPrintTemplateValue
  layout?: SuperTicketPreview['layout']
  clientJobId?: string
  onIntentReleased?: () => void
  paperHasPreprintedBranding?: boolean
  /** The ticket already rendered for the browser, opened when the local printer can't take it. */
  browserFallbackHtml?: string
}

/**
 * The receipt falls back to the item's live product name. The order's variant is untyped until the
 * products domain converts, so read just that name here.
 */
function readReceiptVariant(
  variant: unknown
): { product: { name?: string | null } | null } | null | undefined {
  if (variant === null || variant === undefined) return variant

  const product = readProperty(variant, 'product')
  const name = readProperty(product, 'name')

  return { product: typeof name === 'string' ? { name } : null }
}

type PrintOrderProps = {
  orderId: number
  /** The condensed trigger's button size and variant. */
  size?: LoadingButtonProps['size']
  variant?: LoadingButtonProps['variant']
  condensed?: boolean
  className?: string
  /** Let the trigger's click reach the parent, as on a card that handles its own clicks. */
  bubbleTriggerClick?: boolean
}

function PrintOrder({
  orderId,
  size,
  variant,
  condensed = true,
  className,
  bubbleTriggerClick = false,
}: PrintOrderProps) {
  const queryClient = useQueryClient()
  const orgId = useRouteOrganizationId()
  const [, setLocalPrintProfileRevision] = useState(0)

  useEffect(() => {
    return subscribeToLocalPrintProfileUpdates(orgId, () => {
      setLocalPrintProfileRevision((revision) => revision + 1)
    })
  }, [orgId])

  const organization = queryClient.getQueryData(organizationQueries.show(orgId).queryKey)
  const localPrintAccessEnabled = hasBetaFeature(organization, BetaFeatureKey.Snapdragon)
  const localOrderSummaryConfigured = hasConfiguredLocalPrint(orgId, 'orderSummary')
  const localSuperTicketConfigured = hasConfiguredLocalPrint(orgId, 'superTicket')
  const localReceiptConfigured =
    localPrintAccessEnabled && hasConfiguredLocalPrint(orgId, 'receipt')

  const { openPrintWindow } = usePrintWindow({
    onError: (error: unknown) =>
      toast.error('Failed to open print window', { description: getErrorMessage(error) }),
  })

  const [isLoading, setIsLoading] = useState(false)
  const [isSuperTicketDialogOpen, setIsSuperTicketDialogOpen] = useState(false)
  const handleTriggerClick = (event: MouseEvent<HTMLButtonElement>) => {
    if (!bubbleTriggerClick) {
      event.stopPropagation()
    }
  }

  const handlePrint = async (
    template: SalesOrderPrintTemplateValue,
    paperHasPreprintedBranding?: boolean
  ) => {
    try {
      setIsLoading(true)
      const orderHtml = await queryClient.fetchQuery(
        salesOrderQueries.print({
          organizationId: orgId,
          salesOrderId: orderId,
          template,
          ...(typeof paperHasPreprintedBranding === 'boolean'
            ? { paperHasPreprintedBranding }
            : {}),
        })
      )

      if (!orderHtml) {
        toast.error('Failed to load order data for printing')
        return false
      }

      void openPrintWindow({ html: orderHtml, autoPrint: true })
      return true
    } catch (error) {
      toast.error('Failed to load order data for printing', { description: getErrorMessage(error) })
      return false
    } finally {
      setIsLoading(false)
    }
  }

  const openBrowserSuperTicket = () => setIsSuperTicketDialogOpen(true)

  const handleSuperTicketPrint = async ({
    html,
    layout,
    template,
    clientJobId,
    onIntentReleased,
  }: SuperTicketPrintRequest) => {
    if (localSuperTicketConfigured) {
      return handleLocalPrint('superTicket', {
        template,
        layout,
        clientJobId,
        onIntentReleased,
        paperHasPreprintedBranding: layout.paper_has_preprinted_branding,
        browserFallbackHtml: html,
      })
    }
    void openPrintWindow({ html, autoPrint: true })
    return true
  }

  const handleMenuSelect = (event: Event, option: PrintOption) => {
    event.stopPropagation()

    if (option.template === SalesOrderPrintTemplate.OrderSummary && localOrderSummaryConfigured) {
      void handleLocalPrint('orderSummary')
      return
    }

    if (option.requiresConfiguration) {
      openBrowserSuperTicket()
      return
    }

    void handlePrint(option.template)
  }

  const handleLocalPrint = async (
    kind: LocalPrintDocumentKind | 'receipt',
    options: LocalPrintOptions = {}
  ) => {
    const { browserFallbackHtml, onIntentReleased, ...printOptions } = options

    try {
      setIsLoading(true)
      if (kind === 'receipt') {
        const salesOrder = await queryClient.fetchQuery(
          salesOrderQueries.show({ organizationId: orgId, salesOrderId: orderId })
        )
        await printLocalReceipt({
          organizationId: orgId,
          salesOrder: {
            ...salesOrder,
            sales_order_items: salesOrder.sales_order_items?.map((item) => ({
              ...item,
              variant: readReceiptVariant(item.variant),
            })),
          },
          reprint: true,
        })
      } else {
        await printLocalSalesOrder({
          organizationId: orgId,
          salesOrderId: orderId,
          kind,
          reprint: true,
          ...printOptions,
        })
      }
      toast.success('Sent directly to the local printer')
      return true
    } catch (error) {
      // A confirmed failure is finished; the next click starts a new printer job.
      const code = readProperty(error, 'code')
      if (code === 'job_failed') onIntentReleased?.()
      const canUseBrowserFallback =
        code === 'local_print_access_disabled' ||
        code === 'job_not_submitted' ||
        (Boolean(readProperty(error, 'httpStatus')) &&
          readProperty(error, 'outcomeUnknown') !== true)
      if (canUseBrowserFallback) {
        if (kind === 'orderSummary') {
          return handlePrint(SalesOrderPrintTemplate.OrderSummary)
        }
        if (kind === 'superTicket') {
          if (browserFallbackHtml) {
            void openPrintWindow({ html: browserFallbackHtml, autoPrint: true })
            return true
          }
          openBrowserSuperTicket()
          return false
        }
      }
      const needsPrinterCheck = localPrintNeedsPrinterCheck(error)
      toast[needsPrinterCheck ? 'warning' : 'error'](
        needsPrinterCheck ? 'Check the printer before printing again' : 'Local print failed',
        localPrintToastOptions(error, getErrorMessage(error), onIntentReleased)
      )
      return false
    } finally {
      setIsLoading(false)
    }
  }

  const trigger = condensed ? (
    <LoadingButton
      variant={variant}
      size={size}
      className={className}
      onClick={handleTriggerClick}
      disabled={isLoading}
      preserveChildren={false}
      aria-label="Print order"
    >
      <Printer className="size-4" />
    </LoadingButton>
  ) : (
    <LoadingButton
      className={cn('flex items-center gap-2', className)}
      onClick={handleTriggerClick}
      disabled={isLoading}
      preserveChildren={false}
    >
      <Printer className="w-6 h-6" />
      Print Order
      <ChevronDown className="size-4" />
    </LoadingButton>
  )

  const menu = (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>{trigger}</DropdownMenuTrigger>
      <DropdownMenuContent align="end" size="sm">
        {printOptions.map((option) => (
          <DropdownMenuItem
            key={option.template}
            disabled={isLoading}
            onSelect={(event) => handleMenuSelect(event, option)}
          >
            {option.label}
          </DropdownMenuItem>
        ))}
        {localReceiptConfigured ? (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem disabled={isLoading} onSelect={() => handleLocalPrint('receipt')}>
              <ReceiptText className="size-4" /> Reprint Receipt
            </DropdownMenuItem>
          </>
        ) : null}
      </DropdownMenuContent>
    </DropdownMenu>
  )

  return (
    <>
      {menu}
      <SuperTicketPrintDialog
        open={isSuperTicketDialogOpen}
        onOpenChange={setIsSuperTicketDialogOpen}
        organizationId={orgId}
        orderId={orderId}
        onPrint={handleSuperTicketPrint}
        onOrderSummary={() =>
          localOrderSummaryConfigured
            ? handleLocalPrint('orderSummary')
            : handlePrint(SalesOrderPrintTemplate.OrderSummary)
        }
      />
    </>
  )
}

export default PrintOrder
